import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, categorySlugMap } from '@/mocks/categories';

const categoryCardImages: Record<string, string> = {
  'sp-1': 'https://readdy.ai/api/search-image?query=Professional%20soccer%20stadium%20with%20dramatic%20lighting%2C%20green%20field%20under%20stadium%20lights%2C%20sports%20atmosphere%2C%20clean%20minimal%20composition&width=800&height=500&seq=cat-sp-1&orientation=landscape',
  'sp-2': 'https://readdy.ai/api/search-image?query=Basketball%20court%20with%20warm%20lighting%2C%20professional%20sports%20venue%2C%20clean%20modern%20arena%2C%20athletic%20atmosphere&width=800&height=500&seq=cat-sp-2&orientation=landscape',
  'sp-3': 'https://readdy.ai/api/search-image?query=Tennis%20court%20with%20clay%20surface%20under%20bright%20sunlight%2C%20professional%20sports%20venue%2C%20clean%20minimal%20scene&width=800&height=500&seq=cat-sp-3&orientation=landscape',
  'sp-4': 'https://readdy.ai/api/search-image?query=Formula%20one%20racing%20track%20with%20speed%20motion%20blur%2C%20professional%20motorsport%2C%20clean%20dynamic%20atmosphere&width=800&height=500&seq=cat-sp-4&orientation=landscape',
  'sp-5': 'https://readdy.ai/api/search-image?query=Esports%20gaming%20arena%20with%20colorful%20LED%20lights%2C%20professional%20gaming%20setup%2C%20modern%20competitive%20atmosphere&width=800&height=500&seq=cat-sp-5&orientation=landscape',
  'sp-6': 'https://readdy.ai/api/search-image?query=Olympic%20stadium%20with%20rings%20symbol%2C%20grand%20sports%20venue%2C%20international%20athletic%20event%2C%20clean%20minimal%20design&width=800&height=500&seq=cat-sp-6&orientation=landscape',
  'en-1': 'https://readdy.ai/api/search-image?query=Home%20cinema%20setup%20with%20warm%20ambient%20lighting%2C%20modern%20entertainment%20system%2C%20cozy%20professional%20atmosphere&width=800&height=500&seq=cat-en-1&orientation=landscape',
  'en-2': 'https://readdy.ai/api/search-image?query=Television%20studio%20with%20professional%20lighting%20setup%2C%20modern%20broadcast%20environment%2C%20clean%20production%20atmosphere&width=800&height=500&seq=cat-en-2&orientation=landscape',
  'en-3': 'https://readdy.ai/api/search-image?query=Music%20concert%20stage%20with%20dramatic%20lighting%2C%20live%20performance%20atmosphere%2C%20professional%20entertainment%20venue&width=800&height=500&seq=cat-en-3&orientation=landscape',
  'en-4': 'https://readdy.ai/api/search-image?query=Modern%20gaming%20room%20with%20RGB%20lighting%2C%20high%20end%20gaming%20pc%20setup%2C%20clean%20minimalist%20gaming%20environment&width=800&height=500&seq=cat-en-4&orientation=landscape',
  'en-5': 'https://readdy.ai/api/search-image?query=Anime%20art%20studio%20with%20drawing%20tablet%20and%20manga%20collection%2C%20creative%20workspace%2C%20warm%20atmospheric%20lighting&width=800&height=500&seq=cat-en-5&orientation=landscape',
  'en-6': 'https://readdy.ai/api/search-image?query=Gourmet%20food%20photography%20with%20warm%20restaurant%20lighting%2C%20elegant%20dining%20setup%2C%20professional%20food%20styling&width=800&height=500&seq=cat-en-6&orientation=landscape',
  'lf-1': 'https://readdy.ai/api/search-image?query=Modern%20urban%20lifestyle%20scene%20with%20coffee%20and%20laptop%2C%20minimal%20workspace%2C%20warm%20natural%20lighting%2C%20contemporary%20interior&width=800&height=500&seq=cat-lf-1&orientation=landscape',
  'lf-2': 'https://readdy.ai/api/search-image?query=Fashion%20clothing%20rack%20with%20elegant%20outfits%2C%20modern%20boutique%20interior%2C%20warm%20lighting%2C%20stylish%20minimal%20atmosphere&width=800&height=500&seq=cat-lf-2&orientation=landscape',
  'lf-3': 'https://readdy.ai/api/search-image?query=Modern%20living%20room%20interior%20with%20natural%20light%2C%20minimalist%20furniture%2C%20warm%20neutral%20tones%2C%20scandinavian%20design&width=800&height=500&seq=cat-lf-3&orientation=landscape',
  'lf-4': 'https://readdy.ai/api/search-image?query=Healthy%20lifestyle%20with%20yoga%20mat%20and%20fresh%20smoothie%2C%20sunlit%20room%2C%20wellness%20atmosphere%2C%20clean%20minimal%20composition&width=800&height=500&seq=cat-lf-4&orientation=landscape',
  'lf-5': 'https://readdy.ai/api/search-image?query=Beautiful%20travel%20destination%20with%20azure%20ocean%20and%20white%20sand%2C%20tropical%20paradise%2C%20serene%20natural%20landscape%2C%20clean%20horizon&width=800&height=500&seq=cat-lf-5&orientation=landscape',
  'lf-6': 'https://readdy.ai/api/search-image?query=Smart%20home%20devices%20on%20clean%20desk%20with%20natural%20light%2C%20modern%20technology%20lifestyle%2C%20minimal%20futuristic%20interior&width=800&height=500&seq=cat-lf-6&orientation=landscape',
  'fi-1': 'https://readdy.ai/api/search-image?query=Financial%20trading%20desk%20with%20multiple%20monitors%20showing%20charts%2C%20professional%20office%2C%20warm%20ambient%20lighting%2C%20clean%20minimal&width=800&height=500&seq=cat-fi-1&orientation=landscape',
  'fi-2': 'https://readdy.ai/api/search-image?query=Digital%20blockchain%20concept%20with%20geometric%20network%20pattern%2C%20modern%20technology%20abstract%2C%20warm%20golden%20tones&width=800&height=500&seq=cat-fi-2&orientation=landscape',
  'fi-3': 'https://readdy.ai/api/search-image?query=Investment%20concept%20with%20growing%20plant%20and%20coins%2C%20financial%20growth%20symbolism%2C%20warm%20professional%20lighting&width=800&height=500&seq=cat-fi-3&orientation=landscape',
  'fi-4': 'https://readdy.ai/api/search-image?query=Corporate%20boardroom%20with%20financial%20reports%20on%20table%2C%20professional%20business%20environment%2C%20warm%20lighting%2C%20clean%20minimal&width=800&height=500&seq=cat-fi-4&orientation=landscape',
  'fi-5': 'https://readdy.ai/api/search-image?query=Startup%20office%20with%20creative%20workspace%2C%20modern%20entrepreneurship%20environment%2C%20warm%20energetic%20atmosphere&width=800&height=500&seq=cat-fi-5&orientation=landscape',
  'fi-6': 'https://readdy.ai/api/search-image?query=Family%20protection%20concept%20with%20house%20model%20and%20shield%2C%20warm%20secure%20atmosphere%2C%20professional%20insurance%20theme&width=800&height=500&seq=cat-fi-6&orientation=landscape',
};

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'sports');

  useEffect(() => {
    if (slug && categorySlugMap[slug]) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  const activeCategory = categorySlugMap[activeSlug];

  const handleTabClick = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.history.pushState(null, '', `/category/${newSlug}`);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('breadcrumb.home'), item: 'https://www.kaiyun.com' },
          { '@type': 'ListItem', position: 2, name: t('nav.category'), item: 'https://www.kaiyun.com/category/sports' },
          { '@type': 'ListItem', position: 3, name: activeCategory?.name || '' },
        ],
      },
      {
        '@type': 'WebPage',
        name: activeCategory?.name + ' - ' + t('schema.org_name'),
        description: activeCategory?.description || '',
        url: `https://www.kaiyun.com/category/${activeSlug}`,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb
          items={[
            { label: t('nav.category'), to: '/category/sports' },
            { label: activeCategory?.name || '' },
          ]}
        />

        <section className="w-full py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {t('categories.title')}
              </h1>
              <p className="text-sm md:text-base text-foreground-500">
                {t('categories.subtitle')}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap items-center justify-center gap-2 mb-10 md:mb-12">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleTabClick(cat.slug)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`} />
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Category Content */}
            {activeCategory && (
              <div>
                <div className="mb-8">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">
                    {activeCategory.name}
                  </h2>
                  <p className="text-sm text-foreground-500">{activeCategory.description}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {activeCategory.content.map((item) => (
                    <div
                      key={item.id}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-default"
                    >
                      <div className="relative w-full h-52 overflow-hidden">
                        <img
                          src={categoryCardImages[item.id] || ''}
                          alt={item.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      </div>
                      <div className="p-4 md:p-5">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}